import Input3DData from "../basic/Input3DData";
import DitapAlert from "./DitapAlert";
import DitapMask from "./DitapMask";
import DitapPopup from "./DitapPopup";

/**
 * 3D 데이터 입력 팝업 UI 클래스.<br>
 * The PopupInput3D is for entering 3D data.
 * @extends DitapPopup
 *
 * @alias PopupInput3D
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Interaction3DPosition} interaction3DPosition 3D 모델 위치 지정을 위한 사용자 입력 클래스.<br> User input class for specifying 3D model position.
 */
class PopupInput3D extends DitapPopup {
  constructor(viewer, interaction3DPosition) {
    const parent = viewer.container;
    super(parent);

    // 입력 박스 생성
    const input3DBox = this.createInput3DBox();

    // 헤더 추가
    this.prependHeader(input3DBox["header"]["title"]);

    // 컨텐츠 추가
    this.appendSubTitle(input3DBox["content"]["subTitle"]);
    this.appendContent(input3DBox["content"]["inputFormBox"]);

    // 푸터 추가
    this.appendFooter(input3DBox["footer"]["footerBox"]);

    // 확인 버튼 바인딩
    this.bindCallbackOkBtn(viewer, interaction3DPosition);

    // 취소 버튼 바인딩
    this.bindCallbackCancelBtn();

    // 탭 클릭 이벤트
    this.addTabClickEvent();

    this._mask = new DitapMask();
  }

  /**
   * "확인" 버튼 요소에 대한 콜백 함수.<br>
   * Callback okay button element.
   * @type {Element}
   */
  get okButton() {
    return this._okButton;
  }
  set okButton(okButton) {
    this._okButton = okButton;
  }

  /**
   * "취소" 버튼 요소에 대한 콜백 함수.<br>
   * Callback cancel button element.
   * @type {Element}
   */
  get cancelButton() {
    return this._cancelButton;
  }
  set cancelButton(cancelButton) {
    this._cancelButton = cancelButton;
  }

  /**
   * 3D 데이터 입력폼 생성.<br>
   * Create a 3D data input form.
   */
  createInput3DBox() {
    const wrapper = this.wrapper;
    wrapper.classList.add("ditap-data-add-popup", "ditap-card-mobile");
    wrapper.classList.remove("ditap-size-popup");

    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "데이터 추가";

    this.closeBtn.classList.add("js-ditap-style-close-btn");

    // # 컨텐츠
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 탭을 선택 후 URL을 입력해주세요.";

    const formBox = document.createElement("div");
    formBox.classList.add("ditap-data-add-wrap");

    // 탭 (3dTiles, gltf)
    const tab = document.createElement("div");
    tab.classList.add("ditap-data-tap-wrap");
    const tabName1 = document.createElement("button");
    tabName1.classList.add(
      "ditap-data-tap",
      "on",
      "js-3dtiles-tab",
      "ditap-custom-button"
    );
    tabName1.innerText = "3D Tiles";
    const tabName2 = document.createElement("button");
    tabName2.classList.add(
      "ditap-data-tap",
      "js-gltf-tab",
      "ditap-custom-button"
    );
    tabName2.innerText = "GLTF";

    tab.appendChild(tabName1);
    tab.appendChild(tabName2);

    // url 입력 폼
    const div = document.createElement("div");
    div.classList.add("ditap-data-url-wrap");
    const h3 = document.createElement("h3");
    h3.classList.add("ditap-data-url-title", "js-ditap-data-url-title");
    h3.innerText = "3D Tiles URL";
    const p = document.createElement("p");
    p.classList.add("ditap-data-url");
    const label = document.createElement("label");
    const input = document.createElement("input");
    input.classList.add("ditap-data-url-inp", "js-ditap-url-inp");
    input.setAttribute("type", "text");
    label.appendChild(input);
    p.appendChild(label);
    div.appendChild(h3);
    div.appendChild(p);

    formBox.appendChild(tab);
    formBox.appendChild(div);

    // # 푸터
    const footerBox = document.createElement("div");
    footerBox.classList.add("ditap-btn-wrap");

    const cancelButton = document.createElement("button");
    cancelButton.classList.add("ditap-popup-btn");
    cancelButton.innerText = "취소";
    footerBox.append(cancelButton);

    const okButton = document.createElement("button");
    okButton.classList.add("ditap-popup-btn", "gold");
    okButton.innerText = "확인";
    footerBox.append(okButton);

    this.okButton = okButton;
    this.cancelButton = cancelButton;
    this.tilesTab = tabName1;
    this.gltfTab = tabName2;

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,
        inputFormBox: formBox,
      },
      footer: {
        footerBox: footerBox,
      },
    };
  }

  /**
   * "확인" 버튼에 대한 콜백 함수 바인딩.<br>
   * Ok callback binding.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {Interaction3DPosition} interaction3DPosition 3D 모델 위치 지정을 위한 사용자 입력 클래스. <br> User input class for specifying 3D model position.
   */
  bindCallbackOkBtn(viewer, interaction3DPosition) {
    this.okButton.addEventListener(
      "click",
      (this.okCallback = (e) => {
        this.okButton.disabled = true; // 버튼 비활성화

        const input = document.getElementsByClassName("js-ditap-url-inp")[0];
        const url = document.getElementsByClassName("js-ditap-url-inp")[0]
          .value;

        if (url.trim().length > 0) {
          let type;
          if (this.tilesTab.classList.contains("on")) {
            type = "3dTiles";
          } else if (this.gltfTab.classList.contains("on")) {
            type = "gltf";
          }

          const inputData = new Input3DData(viewer);
          inputData.url = url;
          inputData.type = type;

          // 타일셋 표출 실패 후 실행 함수
          const callback = () => {
            this.close(); // 팝업 close
          };

          const alertOption = {
            type: "warning",
            title: "데이터 추가",
            buttons: "ONLYOK",
            open: false,
            callback: (alert) => {
              alert.destroy();
            },
          };

          fetch(url)
            .then((response) => {
              if (!response.ok) {
                if (response.status === 400) {
                  alertOption.content1 = "요청이 잘못되었습니다.";
                  alertOption.content2 = "요청 주소를 다시 확인해주세요.";
                } else if (response.status === 401) {
                  alertOption.content1 = "인증이 필요합니다.";
                  alertOption.content2 = "요청 주소를 다시 확인해주세요.";
                } else if (response.status === 404) {
                  alertOption.content1 = "유효하지 않은 URL 입니다.";
                  alertOption.content2 = "올바른 URL을 다시 입력해주세요.";
                } else {
                  alertOption.content1 = "모델 추가 도중 에러가 발생했습니다.";
                  alertOption.content2 = response.statusText;
                }
                const warningAlert = new DitapAlert(alertOption);
                warningAlert.open();

                warningAlert.alertOkBtn.addEventListener("click", () => {
                  warningAlert.destroy();
                  this.okButton.disabled = false;
                });
              } else {
                this.okButton.disabled = false;
                if (type === "gltf") {
                  // 모델 -> 위치 지정 후 뷰어 표출
                  interaction3DPosition.model = inputData;
                  interaction3DPosition.on(); // 인터랙션 on
                  input.value = "";
                  this.close();
                } else if (type === "3dTiles") {
                  // 타일셋 -> 뷰어 표출
                  inputData.createTileset(callback);
                  input.value = "";
                }
              }
            })
            .catch((error) => {
              if (
                error instanceof TypeError &&
                error.message.includes("Access to fetch at")
              ) {
                // CORS 에러인 경우
                console.log("CORS error:", error.message);
                alertOption.content1 = "CORS 에러인 경우";
                alertOption.content2 = "CORS 에러인 경우";
              } else if (
                error instanceof TypeError &&
                error.message.includes("Failed to fetch")
              ) {
                // 잘못된 URL인 경우
                console.log(
                  "Invalid URL or resource not found:",
                  error.message
                );
                alertOption.content1 = "유효하지 않은 URL 입니다.";
                alertOption.content2 = "올바른 URL을 다시 입력해주세요.";
              } else {
                // 기타 일반적인 에러인 경우
                console.log("General error:", error.message);
                alertOption.content1 = "모델 추가 도중 에러가 발생했습니다.";
                alertOption.content2 = "잠시후에 다시 시도해주세요.";
              }

              // input value 초기화
              input.value = "";
              const warningAlert = new DitapAlert(alertOption);
              warningAlert.open();

              warningAlert.alertOkBtn.addEventListener("click", () => {
                warningAlert.destroy();
                this.okButton.disabled = false;
              });
            });
        } else {
          const emptyAlert = new DitapAlert({
            type: "warning",
            title: "데이터 추가",
            content1: "URL을 입력해주세요.",
            buttons: "ONLYOK",
            open: false,
            callback: (alert) => {
              alert.destroy();
            },
          });

          emptyAlert.alertOkBtn.addEventListener("click", () => {
            emptyAlert.destroy();
            this.okButton.disabled = false;
          });

          emptyAlert.open();
        }
      })
    );
  }

  /**
   * "취소" 버튼에 대한 콜백 함수 바인딩.<br>
   * Cancel callback binding.
   */
  bindCallbackCancelBtn() {
    this.cancelButton.addEventListener(
      "click",
      (this.cancelCallback = (e) => {
        this.close();
      })
    );
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    this.removeAllContent();
    this.removeClickEvent();

    this.okCallback = undefined;
    this.cancelCallback = undefined;
    this.tilesTabClickEvent = undefined;
    this.gltfTabClickEvent = undefined;

    this._mask.closeMobile();
    super.destroy();
  }

  /**
   * 콜백 함수 바인딩 해제.<br>
   * Remove callback binding.
   */
  removeCallback() {
    if (this.okCallback) {
      this.okButton.removeEventListener("click", this.okCallback);
    }
    if (this.cancelCallback) {
      this.cancelButton.removeEventListener("click", this.cancelCallback);
    }
  }

  /**
   * 클릭 이벤트 제거.<br>
   * Remove click event.
   */
  removeClickEvent() {
    if (this.tilesTabClickEvent) {
      this.tilesTab.removeEventListener("click", this.tilesTabClickEvent);
    }
    if (this.gltfTabClickEvent) {
      this.gltfTab.removeEventListener("click", this.gltfTabClickEvent);
    }
  }

  /**
   * 데이터 입력 팝업 초기화.<br>
   * Reset the data input popup.
   */
  reset() {
    // input value 초기화
    const input = document.getElementsByClassName("js-ditap-url-inp")[0];

    if (input) {
      input.value = "";

      // 탭 초기화
      this.tilesTab.classList.add("on");
      this.gltfTab.classList.remove("on");
    }
  }

  /*
   * 팝업 열기.</br>
   * Open popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br> element If no parameter is entered, open wrapper.
   */
  open(element) {
    super.open(element);

    this._mask.openMobile();
  }

  /*
   * 팝업 닫기.</br>
   * Close popup.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br>  If no parameter is entered, close wrapper.
   */
  close(element = undefined) {
    this.reset();

    super.close(element);
    this._mask.closeMobile();
  }

  /**
   * 탭 변경이 발생할 때 실행할 이벤트 추가.<br>
   * Add an event to run when tab changes are made
   */
  addTabClickEvent() {
    this.tilesTab.addEventListener(
      "click",
      (this.tilesTabClickEvent = () => {
        const subTitle = document.getElementsByClassName(
          "js-ditap-data-url-title"
        )[0];
        const input = document.getElementsByClassName("js-ditap-url-inp")[0];
        input.value = "";
        subTitle.innerHTML = "3D Tiles URL";
        this.gltfTab.classList.remove("on");
        this.tilesTab.classList.add("on");
      })
    );

    this.gltfTab.addEventListener(
      "click",
      (this.gltfTabClickEvent = () => {
        const subTitle = document.getElementsByClassName(
          "js-ditap-data-url-title"
        )[0];
        const input = document.getElementsByClassName("js-ditap-url-inp")[0];
        input.value = "";
        subTitle.innerHTML = "GLTF URL";
        this.tilesTab.classList.remove("on");
        this.gltfTab.classList.add("on");
      })
    );
  }
}

export default PopupInput3D;
